import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.dashboard');
import { OnInit, OnDestroy } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

declare const window: any;

@Component({
    selector: 'wiz-page-dashboard',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.dashboard/view.scss */
:host {
  display: block;
  height: 100dvh;
  min-height: 100dvh;
  background: rgb(250, 250, 250);
}

.dashboard-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.dashboard-state--screen {
  min-height: 100dvh;
}

.dashboard-state--header {
  min-height: calc(100dvh - 65px);
}

.dashboard-state--content {
  min-height: calc(100dvh - 165px);
}`],
})
export class PageDashboardComponent implements OnInit, OnDestroy {
    constructor(@Inject( Service) public service: Service) { }

    // 상태
    public initialLoading: boolean = true;
    public connected: boolean = false;
    public loading: boolean = false;
    public activeTab: string = 'today';

    // 날짜 네비게이션
    public selectedDate: Date = new Date();
    public weekOffset: number = 0;

    // 달력
    public showCalendar: boolean = false;
    public calendarYear: number = new Date().getFullYear();
    public calendarMonth: number = new Date().getMonth();
    public calendarDays: any[] = [];

    // 커스텀 모달
    public showConfirmModal: boolean = false;
    public confirmModalTitle: string = '';
    public confirmModalMessage: string = '';
    public confirmModalVariant: 'default' | 'danger' = 'default';
    private confirmResolve: ((value: boolean) => void) | null = null;

    // 건강 데이터
    public healthData: any = {
        steps: null,
        heartRate: null,
        sleep: null,
        calories: null
    };
    public exerciseSessions: any[] = [];
    public weeklyAggregate: any = null;
    public weeklySleepSummary: any = {
        totalMinutes: 0,
        daysWithData: 0
    };

    // 브릿지 콜백
    private bridgeCallbackId: number = 0;
    private bridgeCallbacks: Map<number, Function> = new Map();

    public async ngOnInit() {
        await this.service.init();
        this.setupBridgeListener();
        await this.checkConnectionStatus();
        this.initialLoading = false;
        await this.service.render();
    }

    ngOnDestroy() {
        if (window.__healthConnectCallback) {
            delete window.__healthConnectCallback;
        }
    }

    // ── 브릿지 ──

    private setupBridgeListener() {
        window.__healthConnectCallback = (callbackId: number, resultJson: string) => {
            const callback = this.bridgeCallbacks.get(callbackId);
            if (callback) {
                this.bridgeCallbacks.delete(callbackId);
                try {
                    callback(JSON.parse(resultJson));
                } catch {
                    callback({ success: false, error: 'Invalid response' });
                }
            }
        };
    }

    private callBridge(method: string, params?: any): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!window.HealthConnect) {
                reject(new Error('Bridge not available'));
                return;
            }

            const id = ++this.bridgeCallbackId;
            this.bridgeCallbacks.set(id, resolve);

            try {
                window.HealthConnect[method](id, JSON.stringify(params || {}));
            } catch (e) {
                this.bridgeCallbacks.delete(id);
                reject(e);
                return;
            }

            setTimeout(() => {
                if (this.bridgeCallbacks.has(id)) {
                    this.bridgeCallbacks.delete(id);
                    reject(new Error('Bridge call timeout'));
                }
            }, 15000);
        });
    }

    // ── 연동 상태 ──

    public async checkConnectionStatus() {
        try {
            const result = await this.callBridge('checkAvailability');
            console.log('[HC] checkAvailability result', result);

            if (result.available && result.hasPermissions) {
                this.connected = true;
                await this.fetchAllData();
            }
        } catch (e) {
            console.error('[HC] checkConnectionStatus error', e);
        }
    }

    public async toggleConnection() {
        if (this.connected) {
            const confirm = await this.openConfirmModal(
                '연동 해제',
                '헬스 커넥트 연동을 해지할 경우\n운동 데이터를 가져올 수 없어요.\n그래도 해지하시겠어요?',
                'danger'
            );
            if (!confirm) return;
        }

        this.loading = true;
        await this.service.render();

        try {
            if (!this.connected) {
                await this.connect();
            } else {
                await this.disconnect();
            }
        } catch (e: any) {
            await this.openConfirmModal('오류', e.message || '연동 중 오류가 발생했습니다.');
        }

        this.loading = false;
        await this.service.render();
    }

    private async connect() {
        const availability = await this.callBridge('checkAvailability');
        console.log('[HC] connect availability', availability);

        if (!availability.available) {
            const install = await this.openConfirmModal(
                '헬스 커넥트 설치 필요',
                '헬스 커넥트 앱이 설치되어 있지 않습니다.\nGoogle Play 스토어에서 설치하시겠습니까?'
            );

            if (install) {
                try {
                    await this.callBridge('openHealthConnectStore');
                } catch {
                    window.open('https://play.google.com/store/apps/details?id=com.google.android.apps.healthdata', '_blank');
                }
            }
            return;
        }

        const permission = await this.callBridge('requestPermissions', {
            permissions: [
                'READ_STEPS',
                'READ_HEART_RATE',
                'READ_ACTIVE_CALORIES_BURNED',
                'READ_SLEEP',
                'READ_EXERCISE'
            ]
        });

        console.log('[HC] permission result', permission);

        if (!permission.granted) {
            await this.openConfirmModal('권한 필요', '헬스 커넥트 권한이 거부되었습니다.\n설정에서 권한을 허용해주세요.');
            return;
        }

        this.connected = true;
        await this.fetchAllData();
    }

    private async disconnect() {
        try {
            await this.callBridge('revokePermissions');
        } catch { }

        this.connected = false;
        this.healthData = {
            steps: null,
            heartRate: null,
            sleep: null,
            calories: null
        };
        this.exerciseSessions = [];
        this.weeklyAggregate = null;
        this.weeklySleepSummary = {
            totalMinutes: 0,
            daysWithData: 0
        };
    }

    // ── 커스텀 모달 ──

    public openConfirmModal(title: string, message: string, variant: 'default' | 'danger' = 'default'): Promise<boolean> {
        this.confirmModalTitle = title;
        this.confirmModalMessage = message;
        this.confirmModalVariant = variant;
        this.showConfirmModal = true;
        this.service.render();
        return new Promise<boolean>((resolve) => {
            this.confirmResolve = resolve;
        });
    }

    public async confirmModalAction() {
        this.showConfirmModal = false;
        this.confirmModalVariant = 'default';
        if (this.confirmResolve) {
            this.confirmResolve(true);
            this.confirmResolve = null;
        }
        await this.service.render();
    }

    public async confirmModalCancel() {
        this.showConfirmModal = false;
        this.confirmModalVariant = 'default';
        if (this.confirmResolve) {
            this.confirmResolve(false);
            this.confirmResolve = null;
        }
        await this.service.render();
    }

    // ── 날짜 네비게이션 ──

    public async prevDay() {
        this.selectedDate = new Date(this.selectedDate);
        this.selectedDate.setDate(this.selectedDate.getDate() - 1);
        await this.fetchAllData();
    }

    public async nextDay() {
        if (this.isToday()) return;
        this.selectedDate = new Date(this.selectedDate);
        this.selectedDate.setDate(this.selectedDate.getDate() + 1);
        await this.fetchAllData();
    }

    public async prevWeek() {
        this.weekOffset--;
        await this.fetchAllData();
    }

    public async nextWeek() {
        if (this.isCurrentWeek()) return;
        this.weekOffset++;
        await this.fetchAllData();
    }

    public isToday(): boolean {
        const today = new Date();
        return this.selectedDate.getFullYear() === today.getFullYear()
            && this.selectedDate.getMonth() === today.getMonth()
            && this.selectedDate.getDate() === today.getDate();
    }

    public isCurrentWeek(): boolean {
        return this.weekOffset >= 0;
    }

    public getSelectedDateLabel(): string {
        const d = this.selectedDate;
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        return `${d.getMonth() + 1}월 ${d.getDate()}일 (${days[d.getDay()]})`;
    }

    public getWeekRangeLabel(): string {
        const { start, end } = this.getWeekRange();
        return `${start.getMonth() + 1}/${start.getDate()} ~ ${end.getMonth() + 1}/${end.getDate()}`;
    }

    // ── 달력 ──

    public toggleCalendar() {
        this.showCalendar = !this.showCalendar;
        if (this.showCalendar) {
            const ref = this.activeTab === 'week' ? this.getWeekRange().start : this.selectedDate;
            this.calendarYear = ref.getFullYear();
            this.calendarMonth = ref.getMonth();
            this.buildCalendarDays();
        }
        this.service.render();
    }

    public calendarPrevMonth() {
        this.calendarMonth--;
        if (this.calendarMonth < 0) {
            this.calendarMonth = 11;
            this.calendarYear--;
        }
        this.buildCalendarDays();
        this.service.render();
    }

    public calendarNextMonth() {
        const now = new Date();
        const nextMonth = this.calendarMonth + 1;
        const nextYear = nextMonth > 11 ? this.calendarYear + 1 : this.calendarYear;
        const normalizedMonth = nextMonth > 11 ? 0 : nextMonth;
        if (nextYear > now.getFullYear() || (nextYear === now.getFullYear() && normalizedMonth > now.getMonth())) {
            return;
        }
        this.calendarMonth = normalizedMonth;
        this.calendarYear = nextYear;
        this.buildCalendarDays();
        this.service.render();
    }

    public getCalendarMonthLabel(): string {
        return `${this.calendarYear}년 ${this.calendarMonth + 1}월`;
    }

    public isCalendarNextDisabled(): boolean {
        const now = new Date();
        const nextMonth = this.calendarMonth + 1;
        const nextYear = nextMonth > 11 ? this.calendarYear + 1 : this.calendarYear;
        const normalizedMonth = nextMonth > 11 ? 0 : nextMonth;
        return nextYear > now.getFullYear() || (nextYear === now.getFullYear() && normalizedMonth > now.getMonth());
    }

    private buildCalendarDays() {
        const year = this.calendarYear;
        const month = this.calendarMonth;
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const days: any[] = [];
        for (let i = 0; i < firstDay; i++) {
            days.push({ day: null });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            const date = new Date(year, month, d);
            date.setHours(0, 0, 0, 0);
            const isFuture = date.getTime() > today.getTime();
            const isSelected = this.activeTab === 'week'
                ? this.isDateInSelectedWeek(date)
                : (this.selectedDate.getFullYear() === year
                    && this.selectedDate.getMonth() === month
                    && this.selectedDate.getDate() === d);
            const isToday = date.getTime() === today.getTime();
            days.push({ day: d, isFuture, isSelected, isToday });
        }
        this.calendarDays = days;
    }

    private isDateInSelectedWeek(date: Date): boolean {
        const { start, end } = this.getWeekRange();
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return date.getTime() >= start.getTime() && date.getTime() <= end.getTime();
    }

    public async selectCalendarDate(day: number) {
        if (!day) return;
        const date = new Date(this.calendarYear, this.calendarMonth, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date.getTime() > today.getTime()) return;

        if (this.activeTab === 'week') {
            const now = new Date();
            const dayOfWeek = date.getDay();
            const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
            const weekStart = new Date(date);
            weekStart.setDate(date.getDate() - diff);

            const currentWeekStart = new Date(now);
            const currentDay = currentWeekStart.getDay();
            const currentDiff = currentDay === 0 ? 6 : currentDay - 1;
            currentWeekStart.setDate(currentWeekStart.getDate() - currentDiff);
            currentWeekStart.setHours(0, 0, 0, 0);
            weekStart.setHours(0, 0, 0, 0);

            const diffDays = Math.round((weekStart.getTime() - currentWeekStart.getTime()) / (1000 * 60 * 60 * 24));
            this.weekOffset = Math.round(diffDays / 7);
        } else {
            this.selectedDate = date;
        }

        this.showCalendar = false;
        await this.fetchAllData();
    }

    // ── 데이터 조회 ──

    public async fetchAllData() {
        if (!this.connected) return;

        this.loading = true;
        await this.service.render();

        const dayStart = this.getDayStart();
        const dayEnd = this.getDayEnd();
        const sleepStart = this.getSleepStart();
        const { start: weekStartDate, end: weekEndDate } = this.getWeekRange();
        const weekStart = this.toBridgeISOString(weekStartDate);
        const weekEnd = this.isCurrentWeek()
            ? this.toBridgeISOString(new Date())
            : this.toBridgeISOString(weekEndDate);
        const weeklySleepStart = this.getWeeklySleepStart(weekStartDate);

        console.log('[HC] range dayStart', dayStart);
        console.log('[HC] range dayEnd', dayEnd);
        console.log('[HC] range sleepStart', sleepStart);
        console.log('[HC] range weekStart', weekStart);
        console.log('[HC] range weekEnd', weekEnd);

        try {
            const todayParams = {
                types: ['STEPS', 'HEART_RATE', 'ACTIVE_CALORIES_BURNED'],
                startDate: dayStart,
                endDate: dayEnd
            };

            const sleepParams = {
                types: ['SLEEP_SESSION'],
                startDate: sleepStart,
                endDate: dayEnd
            };

            console.log('[HC] readHealthData today params', todayParams);
            const todayData = await this.callBridge('readHealthData', todayParams);
            console.log('[HC] readHealthData today result', todayData);

            console.log('[HC] readHealthData sleep params', sleepParams);
            const sleepData = await this.callBridge('readHealthData', sleepParams);
            console.log('[HC] readHealthData sleep result', sleepData);

            this.healthData = {
                steps: this.normalizeSteps(todayData?.steps),
                heartRate: this.normalizeHeartRate(todayData?.heartRate),
                sleep: this.normalizeSleep(sleepData?.sleep),
                calories: this.normalizeCalories(todayData?.calories)
            };

            console.log('[HC] normalized healthData', this.healthData);
        } catch (e) {
            console.error('[HC] Health data fetch error', e);
        }

        try {
            const sessionParams = {
                startDate: dayStart,
                endDate: dayEnd
            };

            console.log('[HC] readExerciseSessions params', sessionParams);
            const sessions = await this.callBridge('readExerciseSessions', sessionParams);
            console.log('[HC] readExerciseSessions result', sessions);

            this.exerciseSessions = sessions?.success ? (sessions.sessions || []) : [];
        } catch (e) {
            console.error('[HC] Exercise sessions fetch error', e);
            this.exerciseSessions = [];
        }

        try {
            const aggregateParams = {
                metrics: [
                    'STEPS_TOTAL',
                    'CALORIES_TOTAL',
                    'EXERCISE_DURATION_TOTAL',
                    'HEART_RATE_AVG',
                    'SLEEP_DURATION_TOTAL'
                ],
                startDate: weekStart,
                endDate: weekEnd
            };

            console.log('[HC] readAggregateData params', aggregateParams);
            const agg = await this.callBridge('readAggregateData', aggregateParams);
            console.log('[HC] readAggregateData result', agg);

            this.weeklyAggregate = agg?.success ? agg : null;
        } catch (e) {
            console.error('[HC] Aggregate data fetch error', e);
            this.weeklyAggregate = null;
        }

        try {
            const weeklySleepParams = {
                types: ['SLEEP_SESSION'],
                startDate: weeklySleepStart,
                endDate: weekEnd
            };

            console.log('[HC] readHealthData weekly sleep params', weeklySleepParams);
            const weeklySleepData = await this.callBridge('readHealthData', weeklySleepParams);
            console.log('[HC] readHealthData weekly sleep result', weeklySleepData);

            this.weeklySleepSummary = this.buildWeeklySleepSummary(
                weeklySleepData?.sleep,
                weekStartDate,
                this.isCurrentWeek() ? new Date() : weekEndDate
            );
        } catch (e) {
            console.error('[HC] Weekly sleep data fetch error', e);
            this.weeklySleepSummary = {
                totalMinutes: 0,
                daysWithData: 0
            };
        }

        this.loading = false;
        await this.service.render();
    }

    public async refreshData() {
        await this.fetchAllData();
    }

    // ── 탭 전환 ──

    public async switchTab(tab: string) {
        this.activeTab = tab;
        this.showCalendar = false;
        await this.service.render();
    }

    // ── 날짜 유틸 ──

    private toBridgeISOString(date: Date): string {
        return date.toISOString();
    }

    private getDayStart(): string {
        const d = new Date(this.selectedDate);
        d.setHours(0, 0, 0, 0);
        return this.toBridgeISOString(d);
    }

    private getDayEnd(): string {
        if (this.isToday()) {
            return this.toBridgeISOString(new Date());
        }
        const d = new Date(this.selectedDate);
        d.setHours(23, 59, 59, 999);
        return this.toBridgeISOString(d);
    }

    private getSleepStart(): string {
        const d = new Date(this.selectedDate);
        d.setDate(d.getDate() - 1);
        d.setHours(18, 0, 0, 0);
        return this.toBridgeISOString(d);
    }

    private getWeeklySleepStart(weekStart: Date): string {
        const d = new Date(weekStart);
        d.setDate(d.getDate() - 1);
        d.setHours(18, 0, 0, 0);
        return this.toBridgeISOString(d);
    }

    private getWeekRange(): { start: Date, end: Date } {
        const d = new Date();
        d.setDate(d.getDate() + this.weekOffset * 7);
        const day = d.getDay();
        const diff = day === 0 ? 6 : day - 1;
        const start = new Date(d);
        start.setDate(d.getDate() - diff);
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start, end };
    }

    // ── 응답 정규화 ──

    private normalizeSteps(steps: any) {
        if (steps == null) return null;

        if (Array.isArray(steps)) {
            const total = steps.reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
            return { count: total };
        }

        if (typeof steps === 'number') {
            return { count: steps };
        }

        if (typeof steps === 'object') {
            return {
                ...steps,
                count: steps.count ?? 0
            };
        }

        return null;
    }

    private normalizeHeartRate(heartRate: any) {
        if (heartRate == null) return null;

        if (Array.isArray(heartRate)) {
            const values = heartRate
                .map(item => Number(item?.bpm))
                .filter(v => !Number.isNaN(v));

            if (values.length === 0) return { avg: null };

            const avg = values.reduce((a, b) => a + b, 0) / values.length;
            return { avg };
        }

        if (typeof heartRate === 'number') {
            return { avg: heartRate };
        }

        if (typeof heartRate === 'object') {
            return {
                ...heartRate,
                avg: heartRate.avg ?? null
            };
        }

        return null;
    }

    private normalizeSleep(sleep: any) {
        if (sleep == null) return null;

        if (Array.isArray(sleep)) {
            const totalMinutes = sleep.reduce((sum, item) => {
                if (!item?.startTime || !item?.endTime) return sum;
                const start = new Date(item.startTime).getTime();
                const end = new Date(item.endTime).getTime();
                if (Number.isNaN(start) || Number.isNaN(end)) return sum;
                return sum + Math.max(0, Math.round((end - start) / 60000));
            }, 0);

            return { totalMinutes };
        }

        if (typeof sleep === 'number') {
            return { totalMinutes: sleep };
        }

        if (typeof sleep === 'object') {
            return {
                ...sleep,
                totalMinutes: sleep.totalMinutes ?? 0
            };
        }

        return null;
    }

    private normalizeCalories(calories: any) {
        if (calories == null) return null;

        if (Array.isArray(calories)) {
            const total = calories.reduce((sum, item) => sum + (Number(item?.kcal) || 0), 0);
            return { total };
        }

        if (typeof calories === 'number') {
            return { total: calories };
        }

        if (typeof calories === 'object') {
            return {
                ...calories,
                total: calories.total ?? 0
            };
        }

        return null;
    }

    // ── 화면 포맷 ──

    public formatSleepDuration(minutes: number): string {
        if (!minutes) return '-';
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${h}시간 ${m}분`;
    }

    public getWeeklyAverageSleepDuration(): string {
        const totalMinutes = this.weeklySleepSummary?.totalMinutes;
        const days = this.weeklySleepSummary?.daysWithData || 0;
        if (!totalMinutes) return '-';
        if (days <= 0) return '-';

        return this.formatSleepDuration(Math.round(totalMinutes / days));
    }

    public formatDuration(seconds: number): string {
        if (seconds == null || seconds <= 0) return '-';

        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);

        if (h > 0) return `${h}시간 ${m}분 ${s}초`;
        if (m > 0) return `${m}분 ${s}초`;
        return `${s}초`;
    }

    public getExerciseDisplayName(session: any): string {
        if (session?.displayName) return session.displayName;
        if (session?.title) return session.title;
        if (session?.exerciseTypeLabel) return session.exerciseTypeLabel;
        return this.getExerciseTypeLabel(session?.exerciseType);
    }

    public formatCalories(cal: number): string {
        if (!cal) return '-';
        return Math.round(cal).toLocaleString();
    }

    public formatDate(dateStr: string): string {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    }

    private buildWeeklySleepSummary(sleep: any, weekStart: Date, weekEnd: Date) {
        const sessions = this.normalizeSleepSessions(sleep);
        const start = new Date(weekStart);
        const end = new Date(weekEnd);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        let totalMinutes = 0;
        const dayKeys = new Set<string>();

        for (const session of sessions) {
            if (!session?.startTime || !session?.endTime) continue;

            const sessionStart = new Date(session.startTime);
            const sessionEnd = new Date(session.endTime);
            if (Number.isNaN(sessionStart.getTime()) || Number.isNaN(sessionEnd.getTime())) continue;

            const targetDate = new Date(sessionEnd);
            targetDate.setHours(0, 0, 0, 0);

            if (targetDate.getTime() < start.getTime() || targetDate.getTime() > end.getTime()) continue;

            totalMinutes += Math.max(0, Math.round((sessionEnd.getTime() - sessionStart.getTime()) / 60000));
            dayKeys.add(this.toDateKey(targetDate));
        }

        return {
            totalMinutes,
            daysWithData: dayKeys.size
        };
    }

    private normalizeSleepSessions(sleep: any): any[] {
        if (Array.isArray(sleep)) return sleep;
        if (sleep?.sessions && Array.isArray(sleep.sessions)) return sleep.sessions;
        if (sleep && typeof sleep === 'object' && sleep.startTime && sleep.endTime) return [sleep];
        return [];
    }

    private toDateKey(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    public getExerciseIcon(type: string): string {
        const map: any = {
            'WALKING': 'fa-solid fa-person-walking',
            'RUNNING': 'fa-solid fa-person-running',
            'CYCLING': 'fa-solid fa-person-biking',
            'HIKING': 'fa-solid fa-person-hiking',
            'SWIMMING': 'fa-solid fa-person-swimming',
            'BADMINTON': 'fa-solid fa-table-tennis-paddle-ball',
            'TENNIS': 'fa-solid fa-table-tennis-paddle-ball',
            'FOOTBALL': 'fa-solid fa-futbol',
            'BASKETBALL': 'fa-solid fa-basketball',
            'STRENGTH_TRAINING': 'fa-solid fa-dumbbell',
            'STRETCHING': 'fa-solid fa-child-reaching',
            'YOGA': 'fa-solid fa-spa',
            'DANCING': 'fa-solid fa-music',
            'ELLIPTICAL': 'fa-solid fa-ellipsis',
            'ROWING': 'fa-solid fa-water',
            'SKIING': 'fa-solid fa-person-skiing'
        };
        return map[type] || 'fa-solid fa-dumbbell';
    }

    public getExerciseTypeLabel(type: string): string {
        const map: any = {
            'RUNNING': '달리기',
            'WALKING': '걷기',
            'CYCLING': '자전거',
            'SWIMMING': '수영',
            'HIKING': '등산',
            'YOGA': '요가',
            'BADMINTON': '배드민턴',
            'TENNIS': '테니스',
            'FOOTBALL': '축구',
            'BASKETBALL': '농구',
            'DANCING': '댄스',
            'STRENGTH_TRAINING': '근력 운동',
            'STRETCHING': '스트레칭',
            'ELLIPTICAL': '일립티컬',
            'ROWING': '로잉',
            'SKIING': '스키',
            'OTHER': '기타 운동'
        };
        return map[type] || type || '운동';
    }
}

export default PageDashboardComponent;